import {assign, sendTo, setup, type AnyEventObject } from 'xstate'
import {websocketCallback} from './websockets_machine'
import {
	Player, 
	Team,
	Client_Event_Name,
	Host_Event_Name,
	Server_Event_Name,
	Server_State_Name,
	Game_Update_Event,
	Host_Resume_Event,
	Host_Update_Event,
	Subject_Listing, 
	Trivia_Fields,
} from '@quiz-buzz/common'
import {ReactElement, createElement} from 'react'
import { Notifier, Websocket_Event_Names } from './types'
import { Question } from '@quiz-buzz/common/src/models/Trivia'
import { all_players_are_in_teams } from './host_machine_state_guards'

export interface Host_Context {
	is_trial: boolean
	awaiting_server_response: boolean
	game_code: string
	host_secret: string
	host_email: string
	host_nickname: string
	remaining_game_count: number | undefined
	players: Player[]
	teams: Team[]
	whos_buzzer_is_active: string | undefined
	teams_enabled: boolean
	trivia_topics?: Trivia_Fields
	chosen_trivia_topic?: Subject_Listing
	round_num: number
	question_num: number
	question: Question
	modal_header?: string
	modal_content?: ReactElement
	modal_confirm_action?: () => void
	modal_cancel_action?: () => void
}

export interface EventReceiver {
	send: (event: Event) => void
}

interface Action_Props {
	event: AnyEventObject,
	context: Host_Context
}

const send_host_action = sendTo('websocketCallback', ({event, context}: Action_Props) =>
	add_standard_host_message_fields(event, context),
)

export enum Host_State_Name {
	splash = 'splash',
	connecting_host = 'connecting_host',
	registering_host = 'registering_host',
	host_game_unverified = 'host_game_unverified',
	host_purchase_games = 'host_purchase_games',
	host_game_setup = 'host_game_setup',
	host_in_game = 'host_in_game',
	host_finished = 'host_finished',
	host_trial_ended = 'host_trial_ended',
	connection_closed = 'connection_closed',
	reconnected = 'reconnected',
	restore_state = 'restore_state',
}

function add_standard_host_message_fields(
	event: AnyEventObject,
	context: Host_Context,
) {
	return {
		type: event.type,
		detail: {
			...event.detail,
			host_secret: context.host_secret,
			host_email: context.host_email,
			game_code: context.game_code,
		},
	}
}

interface State_Machine_Side_Effect_Functions {
	clear_game_link?: () => void,
	toast?: Notifier,
	buzzer_test_callback?: (name: string) => void
}

const helpers: State_Machine_Side_Effect_Functions = {}

export const set_host_machine_helpers = (new_helpers: State_Machine_Side_Effect_Functions) => {
	helpers.clear_game_link = new_helpers.clear_game_link
	helpers.toast = new_helpers.toast
	helpers.buzzer_test_callback = new_helpers.buzzer_test_callback
}

// export const create_host_machine = (clear_game_link_function: () => void, toast: Notifier, buzzer_test_callback: (name: string) => void, websocketCallback: CallbackActorLogic<EventObject, NonReducibleUnknown, EventObject>) => 
export const host_state_machine = setup({
		types: {
			context: {} as Host_Context,
		},
		actors: {
			websocketCallback,
		},
		actions: {
			log_error: ({event, context}) => {
				const error = {
					error: 'event_error',
					detail: {
						event: event,
						context,
					},
				}
				console.log(error)
			},
			event_error: ({event, self}) => {
				console.log(event)
				if (event.detail?.error_message) {
					helpers.toast?.error(
						event.detail.error_message,
					)
				}
				else {
					helpers.toast?.error(
						`event [${event.type}] from source: [${
							event.detail?.source
						}] not valid in state [${self.getSnapshot().value}]`,
					)
				}
				
			},
			publish_error: ({event}) => {
				helpers.toast?.error(`${event.type}, ${event.detail?.message}`)
			},
			set_trial_mode: assign({
				is_trial: () => true,
			}),
			start_waiting_for_server: assign({
				awaiting_server_response: () => true,
			}),
			stop_waiting_for_server: assign({
				awaiting_server_response: () => false,
			}),
			set_host_nickname: assign({
				host_nickname: ({event}) => event.detail.host_nickname,
			}),
			set_host_email: assign({
				host_email: ({event}) => event.detail.host_email,
			}),
			init_host: assign({
				host_secret: ({event}) => event.detail.host_secret,
				host_email: ({event}) => event.detail.host_email,
				teams_enabled: ({event}) => event.detail.teams_enabled,
				game_code: ({event}) => event.detail.game_code,
			}),
			notify_verification_code_incorrect: () => {
				helpers.toast?.error('verification code incorrect, please check and try again')
			},
			notify_no_games_left: () => {
				helpers.toast?.error('No games left, please purchase one to play')
			},
			notify_payment_email_mismatch_for_existing_game: ({context, event}) => {
				const existing_email = context.host_email?.trim().toLowerCase()
				const payment_email = event.detail?.host_email?.trim().toLowerCase()
				const has_existing_game = !!context.game_code && !!context.host_secret

				if (has_existing_game && existing_email && payment_email && existing_email !== payment_email) {
					helpers.toast?.error('Warning: payment used a different email to this game. Use your original game email to resume this game later.')
				}
			},
			update_game_state: assign({
				players: ({event}) => (event as Game_Update_Event).detail.players,
				teams: ({event}) => (event as Game_Update_Event).detail.teams,
				whos_buzzer_is_active: ({event}) =>
					(event as Game_Update_Event).detail.whos_buzzer_is_active,
				round_num: ({event}) =>
					(event as Game_Update_Event).detail.current_round,
				question_num: ({event}) =>
					(event as Game_Update_Event).detail.current_question,
				teams_enabled: ({event}) =>
					(event as Game_Update_Event).detail.teams_enabled,
			}),
			update_host_state: assign({
				players: ({event}) => (event as Host_Update_Event).detail.players,
				teams: ({event}) => (event as Host_Update_Event).detail.teams,
				remaining_game_count: ({event}) =>
					(event as Game_Update_Event).detail.remaining_game_count,
				whos_buzzer_is_active: ({event}) =>
					(event as Host_Update_Event).detail.whos_buzzer_is_active,
				round_num: ({event}) =>
					(event as Host_Update_Event).detail.current_round,
				question_num: ({event}) =>
					(event as Host_Update_Event).detail.current_question,
				teams_enabled: ({event}) =>
					(event as Host_Update_Event).detail.teams_enabled,
				chosen_trivia_topic:  ({event}) =>
					(event as Host_Update_Event).detail.chosen_trivia_topic,
				question: ({event}) => (event as Host_Update_Event).detail.question,
			}),
			update_host_resume_state: assign({
				host_email: ({event}) => (event as Host_Resume_Event).detail.host_email,
				host_secret: ({event}) => (event as Host_Resume_Event).detail.host_secret,
				game_code: ({event}) => (event as Host_Resume_Event).detail.game_code,
			}),
			update_test_buzzer: ({event}) => {
				helpers.buzzer_test_callback && helpers.buzzer_test_callback(event.detail.player_name)
			},
			update_trivia_topics: assign({
				trivia_topics: ({ event }) => event.detail
			}),
			clear_trivia_topics: assign({
				trivia_topics: () => ({ fields: [] })
			}),
			reset_chosen_trivia_topic: assign({
				chosen_trivia_topic: () => undefined
			}),
			handle_player_buzzed: assign({
				whos_buzzer_is_active: ({event}) => event.detail.whos_buzzer_is_active,
			}),
			set_game_code: assign({
				game_code: ({ event }) => { console.log('setting player game code:', event.detail.game_code); return event.detail.game_code }
			}),
			reset_context: assign({
				game_code: () => '',
				awaiting_server_response: () => false,
				host_email: () => '',
				host_nickname: () => '',
				host_secret: () => '',
				players: () => [],
				teams: () => [],
				round_num: () => 0,
				question_num: () => 0,
				is_trial: false,
				trivia_topics: undefined,
				chosen_trivia_topic: undefined,
				whos_buzzer_is_active: () => undefined,
				question: {
					question_text: '',
					difficulty: 1
				},
				modal_header: () => undefined,
				modal_content: () => undefined,
				modal_confirm_action: () => undefined,
				modal_cancel_action: () => undefined,
			}),
			show_cancel_game_confirmation_modal: assign({
				modal_header: () => 'Cancel game?',
				modal_content: () => createElement('div', undefined, 'Are you sure you want to cancel this game? This cannot be undone.'),
			}),
			clear_modal: assign({
				modal_header: () => undefined,
				modal_content: () => undefined,
				modal_confirm_action: () => undefined,
				modal_cancel_action: () => undefined,
			}),
			clear_game_code_hash: () => {
				helpers.clear_game_link && helpers.clear_game_link()
			},
			notify_feedback_received: () => {
				helpers.toast?.info('feedback received, thanks!')
			},
			notify_websocket_error: () => {
				helpers.toast?.error('Cant connect, game may have ended')
			},
		},
		guards: {
			has_player_details: ({event}) =>
				!!event.detail.player_name && !!event.detail.game_code,
			has_register_host_details: ({event}) =>
				(!!event.detail.host_email),
			has_register_trial_host_details: ({event}) =>
				(!!event.detail.host_nickname),
			all_players_are_in_teams: all_players_are_in_teams,
			no_host_secret: ({context}) =>
				!context.host_secret,
			restore_host_game_unverified: ({event, context}) =>
				event.detail?.state === Server_State_Name.unverified &&
				!!context.host_secret,
			restore_host_purchase_games: ({event, context}) =>
				event.detail?.state === Server_State_Name.has_run_out_of_games &&
				!!context.host_secret,			
			restore_host_game_setup: ({event, context}) =>
				event.detail?.state === Server_State_Name.pre_game_setup &&
				!!context.host_secret,
			restore_host_in_game: ({event, context}) =>
				event.detail?.state === Server_State_Name.in_play &&
				!!context.host_secret,
			restore_host_finished: ({event, context}) =>
				event.detail?.state === Server_State_Name.finished &&
				!!context.host_secret,
		},
	}).createMachine({
		initial: Host_State_Name.splash,
		invoke: {
			id: 'websocketCallback',
			src: 'websocketCallback',
		},
		context: {
			game_code: '',
			is_trial: false,
			awaiting_server_response: false,
			host_secret: '',
			host_email: '',
			host_nickname: '',
			remaining_game_count: 0,
			players: [],
			teams: [],
			teams_enabled: false,
			whos_buzzer_is_active: '',
			question_num: 1,
			round_num: 1,
			question: {
				question_text: '',
				difficulty: 1,
			},
		},
		on: {
			[Server_Event_Name.game_expired]: {
				actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
			},
			[Client_Event_Name.send_feedback]: {
				actions: [
					'start_waiting_for_server',
					sendTo('websocketCallback', ({ event, context }) => {
						if (context.host_secret) {
							return add_standard_host_message_fields(event, context)
						}
						return event
					}),
				],
			},
			[Server_Event_Name.feedback_received]: {
				actions: ['stop_waiting_for_server', 'notify_feedback_received']
			},
			'*': {
				actions: ['event_error'],
			},
		},
		states: {
			[Host_State_Name.splash]: {
				entry: ['stop_waiting_for_server', 'reset_context'],
				on: {
					[Client_Event_Name.navigate_back]: {},
					[Client_Event_Name.start_host_registration]: {
						target: Host_State_Name.registering_host,
					},
					[Client_Event_Name.start_host_trial_registration]: {
						actions: ['reset_context', 'set_trial_mode'],
						target: Host_State_Name.registering_host,
					},
					[Host_Event_Name.purchase_full_game]: {
						target: Host_State_Name.host_purchase_games,
					},
					[Websocket_Event_Names.websocket_closed]: {
					},
				},
			},
			[Host_State_Name.connection_closed]: {
				entry: ['stop_waiting_for_server'],
				on: {
					[Client_Event_Name.navigate_back]: {
						actions: ['reset_context', 'clear_game_code_hash'],
						target: 'splash',
					},
					[Client_Event_Name.reset_game]: {
						target: 'splash',
					},
					[Websocket_Event_Names.connect_websocket]: {
						actions: sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.connect_websocket, detail: { game_code: context.game_code } })),
					},
					[Websocket_Event_Names.websocket_opened]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.reconnected,
					},
					[Websocket_Event_Names.websocket_closed]: {
						target: Host_State_Name.connection_closed,
					},
					[Websocket_Event_Names.websocket_error]: {
						actions: ['stop_waiting_for_server', 'notify_websocket_error'],
					},
				},
			},
			[Host_State_Name.reconnected]: {
				entry: [
					'start_waiting_for_server',
					sendTo('websocketCallback', ({context}) => {
						if (context.host_secret) {
							return add_standard_host_message_fields(
								{type: Host_Event_Name.get_host_status},
								context,
							)
						}
					}),
				],
				on: {
					[Client_Event_Name.navigate_back]: {},
					[Server_Event_Name.game_update]: {
						actions: ['stop_waiting_for_server', 'update_game_state'],
						target: Host_State_Name.restore_state,
					},
					[Server_Event_Name.host_update]: {
						actions: ['stop_waiting_for_server', 'update_host_state'],
						target: Host_State_Name.restore_state,
					},
					[Server_Event_Name.game_doesnt_exist]: {
						actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
						target: Host_State_Name.splash,
					},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
				},
			},
			[Host_State_Name.restore_state]: {
				entry: ['stop_waiting_for_server'],
				always: [
					{
						guard: 'restore_host_game_unverified',
						target: Host_State_Name.host_game_unverified,
					},
					{
						guard: 'restore_host_purchase_games',
						target: Host_State_Name.host_purchase_games,
					},
					{
						guard: 'restore_host_game_setup',
						target: Host_State_Name.host_game_setup,
					},
					{guard: 'restore_host_in_game', target: Host_State_Name.host_in_game},
					{
						guard: 'restore_host_finished',
						target: Host_State_Name.host_finished,
					},
					{guard: 'no_host_secret', target: Host_State_Name.registering_host},
				],
				on: {
					[Websocket_Event_Names.websocket_closed]: {
						target: Host_State_Name.connection_closed,
					},
				},
			},
			[Host_State_Name.registering_host]: {
				on: {
					[Server_Event_Name.game_in_setup]: {
							target: Host_State_Name.host_game_setup,
					},
					[Client_Event_Name.navigate_back]: {
					},
					[Host_Event_Name.register_host]: {
						guard: 'has_register_host_details',
						actions: ['set_host_email', 'start_waiting_for_server', sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.create_new_server, detail: { is_trial: false, host_email: context.host_email } }))],
					},
					[Host_Event_Name.register_trial_host]: {
						guard: 'has_register_trial_host_details',
						actions: ['set_host_nickname', 'start_waiting_for_server', sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.create_new_server, detail: { is_trial: true, host_nickname: context.host_nickname } }))],
					},
					[Server_Event_Name.game_server_created]: {
						actions: ['stop_waiting_for_server', 'init_host', 'start_waiting_for_server', sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.connect_websocket, detail: { game_code: context.game_code } }))]
					},
					[Websocket_Event_Names.websocket_opened]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_game_setup,
					},
					[Host_Event_Name.purchase_full_game]: {
						target: Host_State_Name.host_purchase_games,
					},
					[Server_Event_Name.invalid_message]: {
						actions: ['stop_waiting_for_server', 'publish_error'],
					},
					[Server_Event_Name.host_registered]: {
						actions: ['stop_waiting_for_server', 'notify_payment_email_mismatch_for_existing_game', 'init_host'],
					},
					[Server_Event_Name.game_setup_unverified]: {
						actions: ['stop_waiting_for_server', 'set_host_email'],
						target: Host_State_Name.host_game_unverified,
					},
					[Host_Event_Name.get_host_status]: {
						actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event}) => event)],
					},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
					[Server_Event_Name.game_doesnt_exist]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.splash,
					},
				},
			},
			[Host_State_Name.connecting_host]: {
				entry: ['start_waiting_for_server', sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.connect_websocket, detail: { game_code: context.game_code } }))],
				on: {
					[Websocket_Event_Names.websocket_opened]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.registering_host,
					},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.splash,
					},
				},
			},
			[Host_State_Name.host_game_unverified]: {
				on: {
					[Client_Event_Name.verify]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.verification_code_incorrect]: {
						actions: ['stop_waiting_for_server', 'notify_verification_code_incorrect'],
					},
					[Server_Event_Name.verify_and_continue]: {
						actions: [
							'stop_waiting_for_server',
							'update_host_resume_state', 
							'start_waiting_for_server',
							sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.connect_websocket, detail: { game_code: context.game_code } })),
						]
					},
					[Websocket_Event_Names.websocket_opened]: {
						actions: ['start_waiting_for_server', sendTo('websocketCallback', ({ context }) => ({ type: Host_Event_Name.get_host_status, detail: { host_secret: context.host_secret } }))],
					},
					[Server_Event_Name.host_update]: [
						{
							guard: ({event}) => event.detail.state === Server_State_Name.in_play,
							actions: ['stop_waiting_for_server', 'update_host_state'],
							target: Host_State_Name.host_in_game,
						},
						{
							actions: ['stop_waiting_for_server', 'update_host_state'],
							target: Host_State_Name.host_game_setup,
						},
					],
					[Server_Event_Name.game_doesnt_exist]: {
						actions: ['stop_waiting_for_server', 'notify_no_games_left'],
						target: Host_State_Name.host_purchase_games,
					},
					[Server_Event_Name.game_in_setup]: {
						target: Host_State_Name.host_game_setup,
					},
					[Client_Event_Name.cancel_game]: {
						actions: ['show_cancel_game_confirmation_modal'],
					},
					[Client_Event_Name.confirm_cancel_game]: {
						actions: ['clear_modal'],
						target: Host_State_Name.splash,
					},
					[Client_Event_Name.dismiss_modal]: {
						actions: ['clear_modal'],
					},
					[Server_Event_Name.game_closed]: {
						target: Host_State_Name.splash,
					},
					[Client_Event_Name.navigate_back]: {},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
				},
			},
			[Host_State_Name.host_purchase_games]: {
				on: {
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
					[Client_Event_Name.buy_games]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.game_in_setup]: {
						target: Host_State_Name.host_game_setup,
					},
					[Client_Event_Name.cancel_purchase]: {
						target: Host_State_Name.splash
					},
					[Server_Event_Name.game_finished]: {
						target: Host_State_Name.splash
					},
					[Client_Event_Name.start_host_registration]: {
						target: Host_State_Name.registering_host,
					},
					[Client_Event_Name.cancel_game]: {
						actions: ['show_cancel_game_confirmation_modal'],
					},
					[Client_Event_Name.confirm_cancel_game]: {
						actions: ['clear_modal', 'start_waiting_for_server', send_host_action],
					},
					[Client_Event_Name.dismiss_modal]: {
						actions: ['clear_modal'],
					},
					[Server_Event_Name.game_closed]: {
						actions: [
							'stop_waiting_for_server',
							sendTo('websocketCallback', () => ({
								type: Websocket_Event_Names.client_request_disconnect,
							})),
							'reset_context',
							'clear_game_code_hash',
						],
						target: Host_State_Name.splash,
					},
				}
			},
			[Host_State_Name.host_game_setup]: {
				entry: [
					'start_waiting_for_server',
					sendTo('websocketCallback', ({context}) => 
						add_standard_host_message_fields(
							{type: Host_Event_Name.get_host_status},
							context,
						)),
				],
				on: {
					[Client_Event_Name.navigate_back]: {
					},
					[Client_Event_Name.cancel_game]: {
						actions: ['show_cancel_game_confirmation_modal'],
					},
					[Client_Event_Name.confirm_cancel_game]: {
						actions: ['clear_modal', 'start_waiting_for_server', send_host_action],
					},
					[Client_Event_Name.dismiss_modal]: {
						actions: ['clear_modal'],
					},
					[Server_Event_Name.game_finished]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_finished,
					},
					[Server_Event_Name.game_update]: {
						actions: ['stop_waiting_for_server', 'update_game_state'],
					},
					[Server_Event_Name.host_update]: {
						actions: ['stop_waiting_for_server', 'update_host_state'],
					},
					[Host_Event_Name.set_teams_enabled]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Client_Event_Name.add_team]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.remove_team]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.remove_player]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.save_and_exit]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.game_saved]: {
						actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
						target: Host_State_Name.splash,
					},
					[Host_Event_Name.start_game]: {
						guard: 'all_players_are_in_teams',
						actions: [send_host_action],
					},
					[Server_Event_Name.game_started]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_in_game,
					},
					[Host_Event_Name.get_host_status]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
					[Server_Event_Name.game_doesnt_exist]: {
						actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
						target: Host_State_Name.splash,
					},
					[Server_Event_Name.buzzer_test]: {
						actions: ['stop_waiting_for_server', 'update_test_buzzer'],
					},
					[Server_Event_Name.trial_timeout]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_trial_ended,
					},
				},
			},
			[Host_State_Name.host_in_game]: {
				on: {
					[Server_Event_Name.host_update]: {
						actions: ['stop_waiting_for_server', 'update_host_state'],
					},
					[Host_Event_Name.get_trivia_topics]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.send_trivia_subjects]: {
						actions: ['stop_waiting_for_server', 'update_trivia_topics']
					},
					[Host_Event_Name.choose_trivia_topic]: {
						actions: [send_host_action, 'clear_trivia_topics'],
					},
					[Server_Event_Name.player_buzzed]: {
						actions: ['stop_waiting_for_server', 'handle_player_buzzed'],
					},
					[Client_Event_Name.clear_buzz]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.get_host_status]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
					[Host_Event_Name.finish_round]: {
						actions: ['reset_chosen_trivia_topic', send_host_action],
					},
					[Server_Event_Name.game_doesnt_exist]: {
						target: Host_State_Name.splash,
					},
					[Host_Event_Name.finish_game]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.game_update]: {
						actions: ['stop_waiting_for_server', 'update_game_state'],
					},
					[Server_Event_Name.game_finished]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_finished,
					},
					[Host_Event_Name.change_team_score]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.change_player_score]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.answer_is_correct]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.answer_is_incorrect]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.skip_question]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Host_Event_Name.return_to_setup]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.game_in_setup]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_game_setup,
					},
					[Client_Event_Name.navigate_back]: {
						actions: sendTo('websocketCallback', ({context}) =>
							add_standard_host_message_fields(
								{type: Host_Event_Name.return_to_setup},
								context,
							),
						),
					},
					[Server_Event_Name.trial_timeout]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_trial_ended,
					},
				},
			},
			[Host_State_Name.host_finished]: {
				entry: ['stop_waiting_for_server'],
				on: {
					[Client_Event_Name.navigate_back]: {},
					[Host_Event_Name.close_game]: {
						actions: ['start_waiting_for_server', send_host_action],
					},
					[Server_Event_Name.game_closed]: {
						actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
						target: Host_State_Name.splash,
					},
					[Websocket_Event_Names.websocket_closed]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.connection_closed,
					},
					[Server_Event_Name.trial_timeout]: {
						actions: ['stop_waiting_for_server'],
						target: Host_State_Name.host_trial_ended,
					},
					[Server_Event_Name.host_update]: {},
				},
			},
			[Host_State_Name.host_trial_ended]: {
				entry: [
					'stop_waiting_for_server',
					sendTo('websocketCallback', () => ({
						type: Websocket_Event_Names.client_request_disconnect,
					})),
				],
				on: {
					[Client_Event_Name.navigate_back]: {
						actions: ['reset_context', 'clear_game_code_hash'],
						target: Host_State_Name.splash,
					},
					[Websocket_Event_Names.websocket_closed]: {
					},
				}
			},
		},
	})


